import { useState } from 'react';
import { Upload, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { useInvitation } from '../../contexts/InvitationContext';

export function MigrationBanner() {
  const { 
    hasPendingMigration, 
    localInvitationsToMigrate, 
    migrateLocalInvitations, 
    skipMigration 
  } = useInvitation();
  
  const [isImporting, setIsImporting] = useState(false);
  const [result, setResult] = useState(null);

  if (!hasPendingMigration || localInvitationsToMigrate.length === 0) {
    return null;
  }

  const handleImport = async () => {
    setIsImporting(true);
    const results = await migrateLocalInvitations();
    setResult(results);
    setIsImporting(false);
  };

  const handleSkip = () => {
    skipMigration();
  };

  if (result?.success && result.imported > 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3">
          <CheckCircle className="text-green-500 flex-shrink-0" size={20} />
          <div className="flex-1">
            <p className="text-green-800 font-medium">
              Successfully imported {result.imported} invitation{result.imported !== 1 ? 's' : ''}!
            </p>
            <p className="text-green-600 text-sm">
              Your invitations are now saved to your account.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (result?.errors?.length > 0) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="text-yellow-500 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-yellow-800 font-medium">
              Imported {result.imported} invitation{result.imported !== 1 ? 's' : ''}, but {result.errors.length} failed.
            </p>
            <p className="text-yellow-600 text-sm">
              Some invitations couldn't be imported. They remain in local storage.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-gradient-to-r from-[#D4AF37]/10 to-[#FCE4EC]/30 border border-[#D4AF37]/20 rounded-lg p-4 mb-6"
      data-testid="migration-banner"
    >
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
            <Upload className="text-[#D4AF37]" size={20} />
          </div>
          <div>
            <p className="font-medium text-[#2D2D2D]">
              Import your saved invitations
            </p>
            <p className="text-sm text-[#8A8A8A]">
              You have {localInvitationsToMigrate.length} invitation{localInvitationsToMigrate.length !== 1 ? 's' : ''} saved locally. 
              Import them to your account to access them anywhere.
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            onClick={handleImport}
            disabled={isImporting}
            className="flex-1 sm:flex-initial btn-primary py-2 px-4 text-sm disabled:opacity-50"
            data-testid="import-button"
          >
            {isImporting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="animate-spin" size={16} />
                Importing...
              </span>
            ) : (
              'Import to Library'
            )}
          </button>
          <button
            onClick={handleSkip}
            disabled={isImporting}
            className="p-2 text-[#8A8A8A] hover:text-[#2D2D2D] transition-colors"
            aria-label="Dismiss"
            data-testid="skip-migration"
          >
            <X size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
